// ============================================================
// DATABASE LAYER - SQLite (nativo) + IndexedDB (web fallback)
// ============================================================

import { Capacitor } from '@capacitor/core';
import { SQLiteConnection, SQLiteDBConnection, CapacitorSQLite } from '@capacitor-community/sqlite';
import type {
  Note,
  PersonalTask,
  ProfitTask,
  AppSettings,
  Reminder,
  ID,
} from '@/types';
import { SCHEMA_SQL } from './schema';

// ------------------------------------------------------------
// Interfaccia comune
// ------------------------------------------------------------
export interface DatabaseAdapter {
  init(): Promise<void>;
  // notes
  getAllNotes(): Promise<Note[]>;
  getNote(id: ID): Promise<Note | null>;
  upsertNote(note: Note): Promise<void>;
  deleteNote(id: ID): Promise<void>;
  // reminders
  getAllReminders(): Promise<Reminder[]>;
  getReminder(id: ID): Promise<Reminder | null>;
  upsertReminder(r: Reminder): Promise<void>;
  deleteReminder(id: ID): Promise<void>;
  // personal tasks
  getAllPersonalTasks(): Promise<PersonalTask[]>;
  upsertPersonalTask(task: PersonalTask): Promise<void>;
  deletePersonalTask(id: ID): Promise<void>;
  // profit tasks
  getAllProfitTasks(): Promise<ProfitTask[]>;
  upsertProfitTask(task: ProfitTask): Promise<void>;
  deleteProfitTask(id: ID): Promise<void>;
  // settings
  getSettings(): Promise<AppSettings | null>;
  upsertSettings(settings: AppSettings): Promise<void>;
  // bulk
  replaceAllNotes(notes: Note[]): Promise<void>;
  replaceAllReminders(reminders: Reminder[]): Promise<void>;
  replaceAllPersonalTasks(tasks: PersonalTask[]): Promise<void>;
  replaceAllProfitTasks(tasks: ProfitTask[]): Promise<void>;
  // maintenance
  close(): Promise<void>;
}

// ------------------------------------------------------------
// Helpers di conversione riga DB -> oggetto TS
// ------------------------------------------------------------
function rowToNote(row: Record<string, any>): Note {
  return {
    id: row.id,
    title: row.title,
    content: row.content ?? '',
    color: row.color ?? 'default',
    tags: row.tags ? JSON.parse(row.tags) : [],
    is_pinned: !!row.is_pinned,
    is_archived: !!row.is_archived,
    reminder_id: row.reminder_id ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function rowToReminder(row: Record<string, any>): Reminder {
  return {
    id: row.id,
    note_id: row.note_id ?? null,
    title: row.title,
    body: row.body ?? '',
    time: row.time ?? null,
    days_of_week: row.days_of_week ? JSON.parse(row.days_of_week) : [],
    at: row.at ?? null,
    enabled: !!row.enabled,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function rowToPersonalTask(row: Record<string, any>): PersonalTask {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    status: row.status,
    priority: row.priority ?? 'medium',
    due_date: row.due_date ?? null,
    tags: row.tags ? JSON.parse(row.tags) : [],
    position: row.position ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function rowToProfitTask(row: Record<string, any>): ProfitTask {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    status: row.status,
    priority: row.priority ?? 'medium',
    client_name: row.client_name ?? '',
    expected_revenue: Number(row.expected_revenue ?? 0),
    actual_revenue: Number(row.actual_revenue ?? 0),
    cost: Number(row.cost ?? 0),
    currency: row.currency ?? 'EUR',
    due_date: row.due_date ?? null,
    tags: row.tags ? JSON.parse(row.tags) : [],
    position: row.position ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

// ============================================================
// ADAPTER SQLITE NATIVO
// ============================================================
class SqliteNativeAdapter implements DatabaseAdapter {
  private sqlite = new SQLiteConnection(CapacitorSQLite);
  private db!: SQLiteDBConnection;
  private readonly DB_NAME = 'pira_notes';
  private readonly DB_VERSION = 1;

  async init(): Promise<void> {
    let existing = false;
    try {
      const check = await this.sqlite.isConnection(this.DB_NAME, false);
      existing = !!check?.result;
    } catch {
      existing = false;
    }
    if (existing) {
      this.db = await this.sqlite.retrieveConnection(this.DB_NAME, false);
    } else {
      this.db = await this.sqlite.createConnection(
        this.DB_NAME,
        false,
        'no-encryption',
        this.DB_VERSION,
        false,
      );
    }
    await this.db.open();
    const statements = SCHEMA_SQL.split(';').map((s) => s.trim()).filter((s) => s.length > 0);
    for (const stmt of statements) {
      await this.db.execute(stmt);
    }
  }

  private async query<T = any>(sql: string, params: any[] = []): Promise<T[]> {
    const res = await this.db.query(sql, params);
    return (res?.values ?? []) as T[];
  }
  private async run(sql: string, params: any[] = []): Promise<void> {
    await this.db.run(sql, params);
  }

  async getAllNotes(): Promise<Note[]> {
    const rows = await this.query('SELECT * FROM notes ORDER BY is_pinned DESC, updated_at DESC');
    return rows.map(rowToNote);
  }
  async getNote(id: ID): Promise<Note | null> {
    const rows = await this.query('SELECT * FROM notes WHERE id = ?', [id]);
    return rows[0] ? rowToNote(rows[0]) : null;
  }
  async upsertNote(note: Note): Promise<void> {
    await this.run(
      `INSERT OR REPLACE INTO notes
        (id, title, content, color, tags, is_pinned, is_archived, reminder_id, created_at, updated_at)
       VALUES (?,?,?,?,?,?,?,?,?,?)`,
      [
        note.id, note.title, note.content, note.color, JSON.stringify(note.tags),
        note.is_pinned ? 1 : 0, note.is_archived ? 1 : 0, note.reminder_id,
        note.created_at, note.updated_at,
      ],
    );
  }
  async deleteNote(id: ID): Promise<void> {
    await this.run('DELETE FROM notes WHERE id = ?', [id]);
  }

  async getAllReminders(): Promise<Reminder[]> {
    const rows = await this.query('SELECT * FROM reminders ORDER BY created_at DESC');
    return rows.map(rowToReminder);
  }
  async getReminder(id: ID): Promise<Reminder | null> {
    const rows = await this.query('SELECT * FROM reminders WHERE id = ?', [id]);
    return rows[0] ? rowToReminder(rows[0]) : null;
  }
  async upsertReminder(r: Reminder): Promise<void> {
    await this.run(
      `INSERT OR REPLACE INTO reminders
        (id, note_id, title, body, time, days_of_week, at, enabled, created_at, updated_at)
       VALUES (?,?,?,?,?,?,?,?,?,?)`,
      [
        r.id, r.note_id, r.title, r.body, r.time, JSON.stringify(r.days_of_week), r.at,
        r.enabled ? 1 : 0, r.created_at, r.updated_at,
      ],
    );
  }
  async deleteReminder(id: ID): Promise<void> {
    await this.run('DELETE FROM reminders WHERE id = ?', [id]);
  }

  async getAllPersonalTasks(): Promise<PersonalTask[]> {
    const rows = await this.query('SELECT * FROM personal_tasks ORDER BY position ASC, created_at DESC');
    return rows.map(rowToPersonalTask);
  }
  async upsertPersonalTask(task: PersonalTask): Promise<void> {
    await this.run(
      `INSERT OR REPLACE INTO personal_tasks
        (id, title, description, status, priority, due_date, tags, position, created_at, updated_at)
       VALUES (?,?,?,?,?,?,?,?,?,?)`,
      [
        task.id, task.title, task.description, task.status, task.priority, task.due_date,
        JSON.stringify(task.tags), task.position, task.created_at, task.updated_at,
      ],
    );
  }
  async deletePersonalTask(id: ID): Promise<void> {
    await this.run('DELETE FROM personal_tasks WHERE id = ?', [id]);
  }

  async getAllProfitTasks(): Promise<ProfitTask[]> {
    const rows = await this.query('SELECT * FROM profit_tasks ORDER BY position ASC, created_at DESC');
    return rows.map(rowToProfitTask);
  }
  async upsertProfitTask(task: ProfitTask): Promise<void> {
    await this.run(
      `INSERT OR REPLACE INTO profit_tasks
        (id, title, description, status, priority, client_name, expected_revenue, actual_revenue,
         cost, currency, due_date, tags, position, created_at, updated_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      [
        task.id, task.title, task.description, task.status, task.priority, task.client_name,
        task.expected_revenue, task.actual_revenue, task.cost, task.currency, task.due_date,
        JSON.stringify(task.tags), task.position, task.created_at, task.updated_at,
      ],
    );
  }
  async deleteProfitTask(id: ID): Promise<void> {
    await this.run('DELETE FROM profit_tasks WHERE id = ?', [id]);
  }

  async getSettings(): Promise<AppSettings | null> {
    const rows = await this.query("SELECT value FROM settings WHERE key = 'app_settings'");
    if (!rows[0]) return null;
    try {
      return JSON.parse(rows[0].value) as AppSettings;
    } catch {
      return null;
    }
  }
  async upsertSettings(settings: AppSettings): Promise<void> {
    await this.run(
      `INSERT OR REPLACE INTO settings (key, value) VALUES ('app_settings', ?)`,
      [JSON.stringify(settings)],
    );
  }

  async replaceAllNotes(notes: Note[]): Promise<void> {
    await this.run('DELETE FROM notes');
    for (const n of notes) await this.upsertNote(n);
  }
  async replaceAllReminders(reminders: Reminder[]): Promise<void> {
    await this.run('DELETE FROM reminders');
    for (const r of reminders) await this.upsertReminder(r);
  }
  async replaceAllPersonalTasks(tasks: PersonalTask[]): Promise<void> {
    await this.run('DELETE FROM personal_tasks');
    for (const t of tasks) await this.upsertPersonalTask(t);
  }
  async replaceAllProfitTasks(tasks: ProfitTask[]): Promise<void> {
    await this.run('DELETE FROM profit_tasks');
    for (const t of tasks) await this.upsertProfitTask(t);
  }

  async close(): Promise<void> {
    try { await this.db?.close(); } catch { /* ignore */ }
  }
}

// ============================================================
// ADAPTER WEB (IndexedDB fallback)
// ============================================================
class IndexedDBAdapter implements DatabaseAdapter {
  private dbName = 'pira_notes';
  private version = 1;
  private db!: IDBDatabase;

  async init(): Promise<void> {
    this.db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('notes')) db.createObjectStore('notes', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('reminders')) db.createObjectStore('reminders', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('personal_tasks')) db.createObjectStore('personal_tasks', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('profit_tasks')) db.createObjectStore('profit_tasks', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private tx(store: string, mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(store, mode).objectStore(store);
  }
  private getAll<T>(store: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const req = this.tx(store, 'readonly').getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    });
  }
  private put<T>(store: string, value: T): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = this.tx(store, 'readwrite').put(value as any);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  private del(store: string, key: ID): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = this.tx(store, 'readwrite').delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  private clear(store: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = this.tx(store, 'readwrite').clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async getAllNotes(): Promise<Note[]> {
    const items = await this.getAll<Note>('notes');
    return items.sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0) || b.updated_at.localeCompare(a.updated_at));
  }
  async getNote(id: ID): Promise<Note | null> {
    const r = await this.getAll<Note>('notes').then((xs) => xs.find((n) => n.id === id));
    return r ?? null;
  }
  async upsertNote(note: Note): Promise<void> { await this.put('notes', note); }
  async deleteNote(id: ID): Promise<void> { await this.del('notes', id); }

  async getAllReminders(): Promise<Reminder[]> {
    const items = await this.getAll<Reminder>('reminders');
    return items.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  async getReminder(id: ID): Promise<Reminder | null> {
    const r = await this.getAll<Reminder>('reminders').then((xs) => xs.find((x) => x.id === id));
    return r ?? null;
  }
  async upsertReminder(r: Reminder): Promise<void> { await this.put('reminders', r); }
  async deleteReminder(id: ID): Promise<void> { await this.del('reminders', id); }

  async getAllPersonalTasks(): Promise<PersonalTask[]> {
    const items = await this.getAll<PersonalTask>('personal_tasks');
    return items.sort((a, b) => a.position - b.position);
  }
  async upsertPersonalTask(task: PersonalTask): Promise<void> { await this.put('personal_tasks', task); }
  async deletePersonalTask(id: ID): Promise<void> { await this.del('personal_tasks', id); }

  async getAllProfitTasks(): Promise<ProfitTask[]> {
    const items = await this.getAll<ProfitTask>('profit_tasks');
    return items.sort((a, b) => a.position - b.position);
  }
  async upsertProfitTask(task: ProfitTask): Promise<void> { await this.put('profit_tasks', task); }
  async deleteProfitTask(id: ID): Promise<void> { await this.del('profit_tasks', id); }

  async getSettings(): Promise<AppSettings | null> {
    const r = await this.getAll<{ key: string; value: string }>('settings').then((xs) => xs.find((x) => x.key === 'app_settings'));
    if (!r) return null;
    try { return JSON.parse(r.value) as AppSettings; } catch { return null; }
  }
  async upsertSettings(settings: AppSettings): Promise<void> {
    await this.put('settings', { key: 'app_settings', value: JSON.stringify(settings) });
  }

  async replaceAllNotes(notes: Note[]): Promise<void> {
    await this.clear('notes');
    for (const n of notes) await this.put('notes', n);
  }
  async replaceAllReminders(reminders: Reminder[]): Promise<void> {
    await this.clear('reminders');
    for (const r of reminders) await this.put('reminders', r);
  }
  async replaceAllPersonalTasks(tasks: PersonalTask[]): Promise<void> {
    await this.clear('personal_tasks');
    for (const t of tasks) await this.put('personal_tasks', t);
  }
  async replaceAllProfitTasks(tasks: ProfitTask[]): Promise<void> {
    await this.clear('profit_tasks');
    for (const t of tasks) await this.put('profit_tasks', t);
  }

  async close(): Promise<void> { this.db?.close(); }
}

// ============================================================
// FACTORY
// ============================================================
let _adapter: DatabaseAdapter | null = null;

export function getDatabase(): DatabaseAdapter {
  if (!_adapter) {
    const isNative = Capacitor.isNativePlatform();
    _adapter = isNative ? new SqliteNativeAdapter() : new IndexedDBAdapter();
  }
  return _adapter;
}

export async function initDatabase(): Promise<DatabaseAdapter> {
  const db = getDatabase();
  await db.init();
  return db;
}
